/**
 * Test-image manifest. The team can drop new JPEGs into
 * `public/test-images/` and add an entry here (or in manifest.json) and
 * the next page load will pick them up.
 *
 * `category` is purely informational, used to group rows in the report
 * so the reader can scan "are all positive controls clustered? are all
 * negative controls clustered?" without having to remember which file
 * was which.
 */

export type ImageCategory =
  | "reference" // an actual POI reference photo
  | "negative" // a scene that should NOT match any POI
  | "synthetic" // gradient / solid colour / known-deterministic test
  | "colour-stress"; // saturated red/orange/etc to surface colour-space issues

export interface TestImage {
  id: string;
  url: string;
  category: ImageCategory;
  /** Optional: a per-POI embeddings JSON URL to compute cosine similarity
   *  against. If set, the report will include the max-cosine vs that
   *  reference set, which is the cleanest direct signal of "would this
   *  image trip the runtime detector for that POI." */
  referenceEmbeddingsUrl?: string;
  notes?: string;
}

export interface ImageManifest {
  comment?: string;
  images: TestImage[];
}

const VALID_CATEGORIES: readonly ImageCategory[] = [
  "reference",
  "negative",
  "synthetic",
  "colour-stress",
];

function validateEntry(raw: unknown, idx: number): TestImage {
  // Manifest is explicitly intended to be hand-edited by testers/operators.
  // Validate every field so typos surface as a clear "entry [3] missing id"
  // message instead of a cryptic TypeError deep inside the inference loop.
  if (!raw || typeof raw !== "object") {
    throw new Error(`manifest.images[${idx}] is not an object`);
  }
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== "string" || r.id.length === 0) {
    throw new Error(`manifest.images[${idx}].id must be a non-empty string`);
  }
  if (typeof r.url !== "string" || r.url.length === 0) {
    throw new Error(`manifest.images[${idx}] (${r.id}).url must be a non-empty string`);
  }
  if (typeof r.category !== "string" || !VALID_CATEGORIES.includes(r.category as ImageCategory)) {
    throw new Error(
      `manifest.images[${idx}] (${r.id}).category must be one of: ${VALID_CATEGORIES.join(", ")} (got ${JSON.stringify(r.category)})`,
    );
  }
  if (r.referenceEmbeddingsUrl !== undefined && typeof r.referenceEmbeddingsUrl !== "string") {
    throw new Error(
      `manifest.images[${idx}] (${r.id}).referenceEmbeddingsUrl must be a string when present`,
    );
  }
  if (r.notes !== undefined && typeof r.notes !== "string") {
    throw new Error(`manifest.images[${idx}] (${r.id}).notes must be a string when present`);
  }
  return {
    id: r.id,
    url: r.url,
    category: r.category as ImageCategory,
    referenceEmbeddingsUrl: r.referenceEmbeddingsUrl as string | undefined,
    notes: r.notes as string | undefined,
  };
}

export async function fetchManifest(): Promise<ImageManifest> {
  const resp = await fetch("/test-images/manifest.json", { credentials: "omit" });
  if (!resp.ok) {
    throw new Error(`Failed to load manifest: HTTP ${resp.status}`);
  }
  const data = (await resp.json()) as Partial<ImageManifest>;
  if (!Array.isArray(data.images)) {
    throw new Error("manifest.images is not an array");
  }
  const images = data.images.map((r, i) => validateEntry(r, i));
  const seenIds = new Set<string>();
  for (const img of images) {
    if (seenIds.has(img.id)) {
      throw new Error(`manifest.images contains duplicate id "${img.id}"`);
    }
    seenIds.add(img.id);
  }
  return { comment: data.comment, images };
}
